import React, { FC, useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { Link } from 'gatsby';
import { Spacer } from '../Spacer';
import { Input } from '../forms/Input';
import { Typography } from '../Typography';
import { Panel, PanelHeadingMarginSizes } from '../Panel';
import { currency } from '../../utils';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { UiStateContext } from '../../context/UiStateContext';
import { Button } from '../Button';

// Interfaces

type Views = 'calculator' | 'disclaimer' | 'results';

export interface MortgageEstimatorProps {
  defaultPurchasePrice?: number;
  defaultDepositAmount?: number;
  defaultInterestRate?: number;
  defaultRepaymentTerm?: number;
  border?: boolean;
}

// Styles

const StyledInlineFields = styled.div`
  ${({ theme: { space } }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${space.sm};
  `};
`;

const StyledNumber = styled.span`
  ${({ theme: { space } }) => css`
    white-space: nowrap;
  `};
`;

// CONST
interface PanelConfig {
  heading: string;
  headingMargin: PanelHeadingMarginSizes;
  backgroundColor: ThemeColorsKeys;
}
const PANEL_CONFIG: PanelConfig = {
  heading: 'Mortgage estimator calculator',
  headingMargin: 'double',
  backgroundColor: 'white',
};

interface MortgageEstimatorFormData {
  purchasePrice: number;
  depositAmount: number;
  interestRate: number;
  repaymentTerm: number;
  nonUkResident: boolean;
  effectiveDate: string;
}

// JSX

export const MortgageEstimator: FC<MortgageEstimatorProps> = ({
  defaultPurchasePrice,
  defaultDepositAmount,
  defaultInterestRate,
  defaultRepaymentTerm,
  border,
  ...rest
}) => {
  const { dataLayerPush } = useDataLayerPush();
  const [activeView, setActiveView] = useState<Views>('calculator');
  const [monthlyMortgageRepayment, setMonthlyMortgageRepayment] = useState<string>();
  const [monthlyInterestOnlyPayment, setMonthlyInterestOnlyPayment] = useState<string>();
  const [selectedPurchasePrice, setSelectedPurchasePrice] = useState<string>();
  const [selectedDepositAmount, setSelectedDepositAmount] = useState<string>();
  const [selectedInterestRate, setSelectedInterestRate] = useState<string>();
  const [selectedRepaymentTerm, setSelectedRepaymentTerm] = useState<string>();
  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const formBladeTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;

  const formMethods = useForm<MortgageEstimatorFormData>({
    mode: 'onSubmit',
    defaultValues: {
      purchasePrice: defaultPurchasePrice,
      depositAmount: defaultDepositAmount,
      interestRate: defaultInterestRate,
      repaymentTerm: defaultRepaymentTerm,
    },
  });
  const { register, handleSubmit, watch, formState } = formMethods;
  const { errors, dirtyFields, touchedFields } = formState;

  // Function to calculate the mortage repayments and interest and progress to next state
  const onSubmit = (formData) => {
    const { purchasePrice, depositAmount, interestRate, repaymentTerm } = formData;
    const principal = purchasePrice - depositAmount;
    const months = repaymentTerm * 12;
    const rate = interestRate / 1200;

    // Set values in state for datalayer
    setSelectedPurchasePrice(purchasePrice);
    setSelectedDepositAmount(depositAmount);
    setSelectedInterestRate(interestRate);
    setSelectedRepaymentTerm(repaymentTerm);

    // Calculate Repayments

    const calculateRepayments = (): string => {
      const repayments = rate > 0 ? (rate + rate / ((1 + rate) ** months - 1)) * principal : principal / months;
      return repayments.toFixed(2);
    };
    // alert(calculateRepayments());

    // Calculate Interest

    const calculateInterest = (): string => {
      if (rate <= 0 || months <= 1 || principal <= 0) {
        return '0';
      }
      const monthly = calculateRepayments();
      const power = (1 + rate) ** 0;
      const interest = (principal * power + +monthly * ((power - 1) / rate)) * rate;

      return interest.toFixed(2);
    };

    // Set the result values

    setMonthlyMortgageRepayment(calculateRepayments());
    setMonthlyInterestOnlyPayment(calculateInterest());

    // Set the active state to the disclaimer

    setActiveView('disclaimer');

    // Update data layer

    dataLayerPush({
      event: 'mortgageEstimator',
      meFormStep: 'Estimate repayments',
      mePurchasePrice: purchasePrice,
      meDepositAmount: depositAmount,
      meInterestRate: interestRate,
      meRepaymentTerm: repaymentTerm,
      formValidation: 'success',
      formError: undefined,
    });
  };

  // Function to handle the get in touch click
  const handleGetInTouchClick = () => {
    setActiveBlade('financialServices');
    setActiveBladeTriggerEl(formBladeTriggerEl);
  };

  return (
    <FormProvider {...formMethods}>
      {/* Calculator view */}

      {activeView === 'calculator' && (
        <Panel
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          method="post"
          noValidate
          buttonOne={{
            label: 'Estimate repayments',
            variant: 'outline',
            type: 'submit',
            disableClickTracking: true,
          }}
          {...PANEL_CONFIG}
          {...rest}
          border={border}
        >
          <Spacer spacing="half">
            {/* Purchase price */}

            <Input
              type="number"
              invalid={!!errors.purchasePrice}
              valid={touchedFields.purchasePrice && dirtyFields.purchasePrice && !errors.purchasePrice}
              label="Purchase price"
              id="purchasePrice"
              placeholder="e.g 800000"
              {...register('purchasePrice', { valueAsNumber: true, required: true, min: 0 })}
              required
              prefix="£"
              hideAsterisk
            />

            {/* Deposit amount */}

            <Input
              type="number"
              invalid={!!errors.depositAmount}
              valid={touchedFields.depositAmount && dirtyFields.depositAmount && !errors.depositAmount}
              label="Deposit amount"
              id="depositAmount"
              placeholder="e.g 80000"
              errorMessage="Please enter a value less then the purchase price"
              {...register('depositAmount', {
                valueAsNumber: true,
                required: true,
                min: 0,
                max: watch('purchasePrice'),
              })}
              required
              prefix="£"
              hideAsterisk
            />

            <StyledInlineFields>
              {/* Interest rate */}

              <Input
                type="number"
                invalid={!!errors.interestRate}
                valid={touchedFields.interestRate && dirtyFields.interestRate && !errors.interestRate}
                label="Interest rate"
                id="interestRate"
                {...register('interestRate', { required: true })}
                required
                tooltipText="4.22% is the average initial rate available in the UK based on a 5 year fixed term."
                tooltipInfoIcon
                suffix="%"
                hideAsterisk
              />

              {/* Repayment term */}

              <Input
                type="number"
                invalid={!!errors.repaymentTerm}
                valid={touchedFields.repaymentTerm && dirtyFields.repaymentTerm && !errors.repaymentTerm}
                label="Repayment term"
                id="repaymentTerm"
                {...register('repaymentTerm', { required: true })}
                required
                suffix="Yrs"
                hideAsterisk
              />
            </StyledInlineFields>
          </Spacer>
        </Panel>
      )}

      {/* Disclaimer view */}

      {activeView === 'disclaimer' && (
        <Panel
          buttonOne={{
            label: 'Continue',
            variant: 'outline',
            type: 'button',
            disableClickTracking: true,
            onClick: () => {
              setActiveView('results');
              dataLayerPush({
                event: 'mortgageEstimator',
                meFormStep: 'Continue',
                mePurchasePrice: selectedPurchasePrice,
                meDepositAmount: selectedDepositAmount,
                meInterestRate: selectedInterestRate,
                meRepaymentTerm: selectedRepaymentTerm,
                formValidation: 'success',
                formError: undefined,
              });
            },
          }}
          {...PANEL_CONFIG}
          {...rest}
          border={border}
        >
          <Spacer spacing="half">
            <Typography component="h4" variant="titleSmall">
              Important note
            </Typography>
            <Typography component="p" variant="bodySmall">
              The monthly payment shown is intended as a guide only. The actual amount you will have to pay each month
              could be more or less than this figure.
            </Typography>
            <Typography component="p" variant="bodySmall">
              Please note repayment of an interest only mortgage is the repayment of the interest only. The cost of
              repayment of the amount borrowed would need to be added.
            </Typography>
          </Spacer>
        </Panel>
      )}

      {/* Results view */}

      {activeView === 'results' && (
        <Panel
          buttonOne={{
            label: 'Calculate again',
            variant: 'outline',
            type: 'button',
            disableClickTracking: true,
            onClick: () => {
              setActiveView('calculator');
              dataLayerPush({
                event: 'mortgageEstimator',
                meFormStep: 'Calculate again',
                mePurchasePrice: selectedPurchasePrice,
                meDepositAmount: selectedDepositAmount,
                meInterestRate: selectedInterestRate,
                meRepaymentTerm: selectedRepaymentTerm,
                formValidation: 'success',
                formError: undefined,
              });
            },
          }}
          {...PANEL_CONFIG}
          {...rest}
          border={border}
        >
          <Spacer spacing="half">
            <Typography component="h4" variant="titleSmall">
              Approximate monthly repayments
            </Typography>
            <Spacer as="dl" spacing="quarter">
              <div>
                <Typography component="dt" variant="titleSmall">
                  Repayment mortgage
                </Typography>
                <Typography component="dd" variant="bodyLarge">
                  {monthlyMortgageRepayment && currency(+monthlyMortgageRepayment)}
                </Typography>
              </div>
              <div>
                <Typography component="dt" variant="titleSmall">
                  Interest only payment
                </Typography>
                <Typography component="dd" variant="bodyLarge">
                  {monthlyInterestOnlyPayment && currency(+monthlyInterestOnlyPayment)}
                </Typography>
              </div>
            </Spacer>
            <Spacer spacing="quarter">
              <Typography component="p" variant="bodySmall">
                Find out more about{' '}
                <Link to="/buyers/the-buying-process/budgeting-and-getting-the-right-mortgage/">
                  budgeting and getting the right mortgage
                </Link>{' '}
                in the buyers resources section of our website.
              </Typography>
              <Typography component="p" variant="bodySmall">
                Speak to our expert mortgage consultants to discuss your requirements.
              </Typography>
              <Button variant="link" type="button" onClick={() => handleGetInTouchClick()} ref={formBladeTriggerEl}>
                Get in touch
              </Button>
            </Spacer>
          </Spacer>
        </Panel>
      )}
    </FormProvider>
  );
};
